import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJsApiLoader, Autocomplete, GoogleMap, Marker } from '@react-google-maps/api';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import '../../style/ReportFoundPet.css';
import { useTranslation } from 'react-i18next';
import petSpecies from '../../data/petSpecies';
import foundPetOutcomes from '../../data/foundPetOutcomes';
import i18n from 'i18next';

const MAP_CONTAINER_STYLE = { width: '100%', height: '300px' };
const DEFAULT_CENTER = { lat: 41.3851, lng: 2.1734 };
const MAP_LIBRARIES = ['places', 'maps'];

const ReportFoundPet = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [petType, setPetType] = useState('dog');

    // Generic label translation for pet species and outcomes
    const getTranslatedLabel = (options, value) => {
        const option = options.find(opt => opt.value === value);
        return option ? option.label[i18n.language] || option.label.en : value;
    };
    const [description, setDescription] = useState('');
    const [foundDate, setFoundDate] = useState('');
    const [foundTime, setFoundTime] = useState('');
    const [foundAddress, setFoundAddress] = useState('');
    const [foundCoordinates, setFoundCoordinates] = useState(null);
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [outcome, setOutcome] = useState('left_on_place');
    const [outcomeAddress, setOutcomeAddress] = useState('');
    const [outcomeCoordinates, setOutcomeCoordinates] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [reporterName, setReporterName] = useState('');
    const [reporterEmail, setReporterEmail] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loadingUserData, setLoadingUserData] = useState(true);

    const autocompleteRef = useRef(null);
    const outcomeAutocompleteRef = useRef(null);
    const mapRef = useRef(null);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: MAP_LIBRARIES,
    });

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser && storedUser.token) {
            setIsAuthenticated(true);
            setLoadingUserData(true);
            axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/account/${storedUser._id}`, {
                headers: { Authorization: `Bearer ${storedUser.token}` },
            })
                .then(res => {
                    setEmail(res.data.email);
                    setPhoneNumber(res.data.phoneNumber || '');
                })
                .catch(() => {})
                .finally(() => setLoadingUserData(false));
        } else {
            setIsAuthenticated(false);
            setLoadingUserData(false);
        }
    }, []);

    const handleFoundPlaceChanged = () => {
        const ac = autocompleteRef.current;
        if (!ac) return;
        const place = ac.getPlace();
        if (place.geometry && place.geometry.location) {
            setFoundCoordinates({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            });
            setFoundAddress(place.formatted_address);
        }
    };

    const handleOutcomePlaceChanged = () => {
        const ac = outcomeAutocompleteRef.current;
        if (!ac) return;
        const place = ac.getPlace();
        if (place.geometry && place.geometry.location) {
            setOutcomeCoordinates({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            });
            setOutcomeAddress(place.formatted_address);
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        setImages(files);
        setImagePreviews(files.map((file) => URL.createObjectURL(file)));
    };

    const handleNextStep = () => {
        if (!petType || !foundCoordinates || !foundDate || !foundTime || !description) {
            setError('Please fill all required fields.');
            return;
        }
        if (outcome === 'found_and_left_elsewhere' && !outcomeCoordinates) {
            setError('Please provide the location where the pet was left.');
            return;
        }
        setError(null);
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('petType', petType);
            formData.append('description', description);
            formData.append('foundLocation', JSON.stringify({
                type: 'Point',
                coordinates: [foundCoordinates.lng, foundCoordinates.lat],
                address: foundAddress,
            }));
            formData.append('foundDateTime', new Date(`${foundDate}T${foundTime}`).toISOString());
            formData.append('outcome', outcome);
            if (outcome === 'found_and_left_elsewhere' && outcomeCoordinates) {
                formData.append('outcomeLocation', JSON.stringify({
                    type: 'Point',
                    coordinates: [outcomeCoordinates.lng, outcomeCoordinates.lat],
                    address: outcomeAddress,
                }));
            }
            images.forEach((img) => formData.append('images', img));

            if (isAuthenticated) {
                // Authenticated: only send phone if not present
                if (!phoneNumber) formData.append('reporterPhoneNumber', phoneNumber);
            } else {
                formData.append('reporterName', reporterName);
                formData.append('reporterEmail', reporterEmail);
                formData.append('reporterPhoneNumber', phoneNumber);
            }

            const storedUser = JSON.parse(localStorage.getItem('user'));
            const headers = isAuthenticated && storedUser?.token
                ? { Authorization: `Bearer ${storedUser.token}` }
                : {};

            await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/found-pets`, formData, { headers });
            enqueueSnackbar('Found pet reported successfully!', { variant: 'success' });
            navigate('/found-pets');
        } catch (err) {
            setError('Error reporting found pet.');
        } finally {
            setLoading(false);
        }
    };

    if (loadError) return <div>{t('reportFoundPetPage.loadingMap')}</div>;
    if (!isLoaded) return <div>{t('reportFoundPetPage.loadingMap')}</div>;
    if (loadingUserData) return <div>{t('reportFoundPetPage.loadingUserData')}</div>;

    return (
        <div className="report-found-pet-page">
            <h1>{t('reportFoundPetPage.title')}</h1>
            {error && <p style={{ color: 'red' }}>{t(error)}</p>}
            {loading ? (
                <p style={{ color: 'green' }}>{t('reportFoundPetPage.reporting')}</p>
            ) : (
                <form onSubmit={handleSubmit}>
                    {step === 1 && (
                        <div>
                            <div>
                                <label>{t('reportFoundPetPage.petType')}</label>
                                <select value={petType} onChange={e => setPetType(e.target.value)} required>
                                    {petSpecies.map(species => (
                                        <option key={species.value} value={species.value}>
                                            {getTranslatedLabel(petSpecies, species.value)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label>{t('reportFoundPetPage.locationFound')}</label>
                                <Autocomplete onLoad={ac => (autocompleteRef.current = ac)} onPlaceChanged={handleFoundPlaceChanged}>
                                    <input
                                        type="text"
                                        value={foundAddress}
                                        onChange={e => setFoundAddress(e.target.value)}
                                        placeholder={t('reportFoundPetPage.enterAddress')}
                                        required
                                    />
                                </Autocomplete>
                                <div className="map-preview">
                                    <GoogleMap
                                        mapContainerStyle={MAP_CONTAINER_STYLE}
                                        center={foundCoordinates || DEFAULT_CENTER}
                                        zoom={foundCoordinates ? 15 : 12}
                                        onLoad={map => (mapRef.current = map)}
                                    >
                                        {foundCoordinates && <Marker position={foundCoordinates} />}
                                    </GoogleMap>
                                </div>
                            </div>
                            <div>
                                <label>{t('reportFoundPetPage.dateFound')}</label>
                                <input type="date" value={foundDate} onChange={e => setFoundDate(e.target.value)} required />
                            </div>
                            <div>
                                <label>{t('reportFoundPetPage.timeFound')}</label>
                                <input type="time" value={foundTime} onChange={e => setFoundTime(e.target.value)} required />
                            </div>
                            <div>
                                <label>{t('reportFoundPetPage.description')}</label>
                                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} required />
                            </div>
                            <div>
                                <label>{t('reportFoundPetPage.images')}</label>
                                <input type="file" accept="image/*" multiple onChange={handleImageChange} />
                                <div className="image-previews">
                                    {imagePreviews.map((src, idx) => (
                                        <img key={idx} src={src} alt={`preview-${idx}`} />
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label>{t('reportFoundPetPage.outcome')}</label>
                                <select value={outcome} onChange={e => setOutcome(e.target.value)} required>
                                    {foundPetOutcomes.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {getTranslatedLabel(foundPetOutcomes, opt.value)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {outcome === 'found_and_left_elsewhere' && (
                                <div>
                                    <label>{t('reportFoundPetPage.locationLeft')}</label>
                                    <Autocomplete onLoad={ac => (outcomeAutocompleteRef.current = ac)} onPlaceChanged={handleOutcomePlaceChanged}>
                                        <input
                                            type="text"
                                            value={outcomeAddress}
                                            onChange={e => setOutcomeAddress(e.target.value)}
                                            placeholder={t('reportFoundPetPage.enterAddress')}
                                        />
                                    </Autocomplete>
                                    <div className="map-preview">
                                        <GoogleMap
                                            mapContainerStyle={MAP_CONTAINER_STYLE}
                                            center={outcomeCoordinates || DEFAULT_CENTER}
                                            zoom={outcomeCoordinates ? 15 : 12}
                                        >
                                            {outcomeCoordinates && <Marker position={outcomeCoordinates} />}
                                        </GoogleMap>
                                    </div>
                                </div>
                            )}
                            <div className="step-buttons">
                                <button type="button" onClick={handleNextStep}>{t('reportFoundPetPage.next')}</button>
                            </div>
                        </div>
                    )}
                    {step === 2 && (
                        <div>
                            <h3>{t('reportFoundPetPage.contactInfo')}</h3>
                            {isAuthenticated ? (
                                <>
                                    <div>
                                        <label>{t('reportFoundPetPage.email')}</label>
                                        <p>{email}</p>
                                    </div>
                                    <div>
                                        <label>{t('reportFoundPetPage.phoneNumber')}</label>
                                        <PhoneInput
                                            country={'us'}
                                            value={phoneNumber}
                                            onChange={value => setPhoneNumber(value)}
                                            inputProps={{
                                                name: 'phone',
                                                required: true,
                                                autoFocus: false,
                                            }}
                                            enableSearch
                                            placeholder={t('reportFoundPetPage.enterPhone')}
                                            containerClass="w-full"
                                            inputClass="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            buttonClass="border border-gray-300 rounded-l-md"
                                            dropdownClass="react-phone-dropdown"
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <label>{t('reportFoundPetPage.name')}</label>
                                        <input type="text" value={reporterName} onChange={e => setReporterName(e.target.value)} required />
                                    </div>
                                    <div>
                                        <label>{t('reportFoundPetPage.email')}</label>
                                        <input type="email" value={reporterEmail} onChange={e => setReporterEmail(e.target.value)} required />
                                    </div>
                                    <div>
                                        <label>{t('reportFoundPetPage.phoneNumber')}</label>
                                        <PhoneInput
                                            country={'us'}
                                            value={phoneNumber}
                                            onChange={value => setPhoneNumber(value)}
                                            inputProps={{
                                                name: 'phone',
                                                required: true,
                                                autoFocus: false,
                                            }}
                                            enableSearch
                                            placeholder={t('reportFoundPetPage.enterPhone')}
                                            containerClass="w-full"
                                            inputClass="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                            buttonClass="border border-gray-300 rounded-l-md"
                                            dropdownClass="react-phone-dropdown"
                                        />
                                    </div>
                                </>
                            )}
                            <div className="step-buttons">
                                <button type="button" onClick={() => setStep(1)}>{t('reportFoundPetPage.back')}</button>
                                <button type="submit" disabled={loading}>{loading ? t('reportFoundPetPage.reporting') : t('reportFoundPetPage.reportFoundPet')}</button>
                            </div>
                        </div>
                    )}
                </form>
            )}
        </div>
    );
};

export default ReportFoundPet;